import type { ReactNode } from 'react'
import { Check, Plus } from 'lucide-react'
import { Button } from '@/components/ui/button'
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from '@/components/ui/command'
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover'
import { cn } from '@/lib/utils'
import { useUiState } from '@/hooks/use-ui-state'
import { useViewFilters, type ChipKey } from '@/hooks/use-view-filters'
import { fieldByName, useIssueFields, useRepoMeta } from '@/lib/query'

type ChipDef = {
  key: ChipKey
  label: string
  options: string[]
  /** Value shown when nothing is selected. */
  anyValue?: string
}

function FilterChip({ chip }: { chip: ChipDef }) {
  const filters = useViewFilters()
  const value = filters.get(chip.key)
  const display = value ?? chip.anyValue ?? '*'
  return (
    <Popover>
      <PopoverTrigger asChild>
        <button
          type="button"
          className={cn(
            'border-border-strong text-muted-foreground hover:bg-hover inline-flex h-6 items-center gap-1 rounded-md border border-dashed px-2 font-mono text-[11px] transition-colors',
            value && 'text-foreground border-solid',
          )}
        >
          {chip.label}:<span className="font-semibold">{display}</span>
        </button>
      </PopoverTrigger>
      <PopoverContent className="w-52 p-0" align="start">
        <Command>
          {chip.options.length > 8 && <CommandInput placeholder={`Filter ${chip.label}…`} />}
          <CommandList>
            <CommandEmpty>No options.</CommandEmpty>
            <CommandGroup>
              <CommandItem onSelect={() => filters.set(chip.key, null)}>
                <Check className={cn('size-3.5', value !== null && 'invisible')} />
                {chip.anyValue ?? 'any'}
              </CommandItem>
              {chip.options.map((option) => (
                <CommandItem key={option} onSelect={() => filters.set(chip.key, option)}>
                  <Check className={cn('size-3.5', value !== option && 'invisible')} />
                  {option}
                </CommandItem>
              ))}
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  )
}

export function ViewHeader({
  title,
  meta,
  chips = ['priority', 'client'],
  toolbar,
  tabs,
}: {
  title: string
  meta: ReactNode
  chips?: ChipKey[] | null
  toolbar?: ReactNode
  /** Lens switcher rendered prominently beside the title. */
  tabs?: ReactNode
}) {
  const { setNewIssueOpen } = useUiState()
  const fields = useIssueFields().data
  const repoMeta = useRepoMeta().data

  const defs: Record<ChipKey, ChipDef> = {
    is: { key: 'is', label: 'is', options: ['open', 'closed'], anyValue: 'all' },
    priority: {
      key: 'priority',
      label: 'priority',
      options: fieldByName(fields, 'Priority')?.options.map((o) => o.name) ?? [],
    },
    client: {
      key: 'client',
      label: 'client',
      options: fieldByName(fields, 'Client')?.options.map((o) => o.name) ?? [],
    },
    assignee: {
      key: 'assignee',
      label: 'assignee',
      options: (repoMeta?.assignableUsers ?? []).map((u) => u.login),
    },
  }

  return (
    <div className="border-border flex flex-col gap-2 border-b px-4 pb-2.5 pt-3">
      <div className="flex items-center gap-3">
        <h1 className="text-[15px] font-semibold tracking-tight">{title}</h1>
        {tabs}
        <div className="flex-1" />
        <Button size="sm" className="h-7 text-xs" onClick={() => setNewIssueOpen(true)}>
          <Plus className="size-3.5" />
          New issue
        </Button>
      </div>
      <div className="text-muted-foreground flex items-center gap-2 text-xs">
        <span>{meta}</span>
        <div className="flex-1" />
        {toolbar}
        {chips?.map((key) => <FilterChip key={key} chip={defs[key]} />)}
      </div>
    </div>
  )
}
